import type { OrganizationState } from 'src/store/types.js';
import type { EmployeeViewModel } from '@trinnvis/dabih-api-client';
import type { TutorialContent } from '../outskirts/tutorial-content';

/**
 * Check if employee has any active tutorials (courses or guides)
 * Active means status is PENDING or IN_PROGRESS (not COMPLETED or EXPIRED)
 * For guides: only the LATEST registration counts (older ones are considered cancelled)
 */
export function isTutorialActive(organization: OrganizationState, currentEmployee: EmployeeViewModel | undefined) {
  if (currentEmployee === undefined) {
    return false;
  }

  const tutorials = organization.tutorials || [];

  // Check all tutorials for active participations
  for (const tutorial of tutorials) {
    if (!tutorial.registrations || tutorial.registrations.length === 0) continue;

    if (tutorial.guide) {
      // For guides: only check the LATEST registration (sorted by registrationDate)
      const sortedRegistrations = [...tutorial.registrations].sort(
        (a, b) => new Date(b.registrationDate || 0).getTime() - new Date(a.registrationDate || 0).getTime(),
      );
      const latestRegistration = sortedRegistrations[0];

      if (latestRegistration.participants && latestRegistration.participants.length > 0) {
        const participant = latestRegistration.participants[0];
        if (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS') {
          return true;
        }
      }
    } else {
      // For courses: check all registrations for matching employeeUuid
      for (const registration of tutorial.registrations) {
        if (!registration.participants) continue;

        for (const participant of registration.participants) {
          if (participant.employeeUuid === currentEmployee.uuid) {
            if (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS') {
              return true;
            }
          }
        }
      }
    }
  }

  return false;
}

/**
 * @deprecated Legacy function - use isTutorialActive instead
 */
export function isGuideActive(tutorialState: { currentPart: number; partStates: string[] } | undefined): boolean {
  if (tutorialState === undefined) {
    return false;
  }

  const f = tutorialState.partStates.filter((s) => s === 'PASS');

  return tutorialState.currentPart > 0 && f.length !== tutorialState.partStates.length;
}

/**
 * @deprecated Legacy function - tutorial content now from API
 * Merges the default tutorial with the current state for the employee
 * @param tutorial
 * @param currentEmployeeTutorialState
 */
function _mergeTutorial(
  tutorial: TutorialContent | undefined,
  currentEmployeeTutorialState: { currentPart: number; partStates: string[] } | undefined,
  organizationTutorialState: { currentPart: number; partStates: string[] } | undefined,
) {
  if (tutorial === undefined) return undefined;

  if (currentEmployeeTutorialState === undefined && organizationTutorialState === undefined) {
    return tutorial;
  }

  const sourceForState = organizationTutorialState ?? currentEmployeeTutorialState;

  return {
    ...tutorial,
    currentPart: sourceForState?.currentPart ?? tutorial.currentPart ?? 0,
    parts: tutorial.parts.map((p, i) => ({
      ...p,
      state: sourceForState?.partStates[i] ?? tutorial.parts[i].state ?? '',
    })),
  };
}

/**
 * @deprecated This function is being replaced with async API calls
 * Tutorial content now comes from API via fetchTutorialContent()
 */
export function buildCurrentTutorialContent(
  _currentTutorialId: string | undefined,
  _organization: OrganizationState,
  _currentEmployee: EmployeeViewModel | undefined,
): TutorialContent | undefined {
  // Legacy function - tutorial content now loaded via API
  // See fetchTutorialContent() in actions.ts
  return undefined;
}

/**
 * Find the participant ID for a specific tutorial and current employee
 * Returns undefined if no active participant found for this tutorial
 * For guides: only the LATEST registration counts (older ones are considered cancelled)
 */
export function findParticipantIdForTutorial(
  tutorialId: string,
  organization: OrganizationState,
  currentEmployee: EmployeeViewModel | undefined,
): string | undefined {
  if (!currentEmployee || !organization.tutorials) {
    return undefined;
  }

  const tutorial = organization.tutorials.find((t) => t.id === tutorialId);
  if (!tutorial || !tutorial.registrations || tutorial.registrations.length === 0) {
    return undefined;
  }

  if (tutorial.guide) {
    // For guides: only check the LATEST registration (sorted by registrationDate)
    const sortedRegistrations = [...tutorial.registrations].sort(
      (a, b) => new Date(b.registrationDate || 0).getTime() - new Date(a.registrationDate || 0).getTime(),
    );
    const latestRegistration = sortedRegistrations[0];

    if (latestRegistration.participants && latestRegistration.participants.length > 0) {
      const participant = latestRegistration.participants[0];
      if (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS') {
        return participant.id;
      }
    }
  } else {
    // For courses: find active participant for this employee
    for (const registration of tutorial.registrations) {
      if (!registration.participants) continue;

      for (const participant of registration.participants) {
        if (
          participant.employeeUuid === currentEmployee.uuid &&
          (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS')
        ) {
          return participant.id;
        }
      }
    }
  }

  return undefined;
}

/**
 * Find the participant ID for the current user's active tutorial
 * Priority: Course first (with employeeUuid match), then Guide (no employeeUuid)
 * Returns undefined if no active tutorials found
 * For guides: only the LATEST registration counts (older ones are considered cancelled)
 */
export function findActiveParticipantId(
  organization: OrganizationState,
  currentEmployee: EmployeeViewModel | undefined,
): string | undefined {
  if (!currentEmployee) {
    return undefined;
  }

  const tutorials = organization.tutorials || [];
  let courseParticipantId: string | undefined;
  let guideParticipantId: string | undefined;

  // Look for active course or guide
  for (const tutorial of tutorials) {
    if (!tutorial.registrations || tutorial.registrations.length === 0) continue;

    if (tutorial.guide) {
      // For guides: only check the LATEST registration (sorted by registrationDate)
      if (!guideParticipantId) {
        const sortedRegistrations = [...tutorial.registrations].sort(
          (a, b) => new Date(b.registrationDate || 0).getTime() - new Date(a.registrationDate || 0).getTime(),
        );
        const latestRegistration = sortedRegistrations[0];

        if (latestRegistration.participants && latestRegistration.participants.length > 0) {
          const participant = latestRegistration.participants[0];
          if (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS') {
            guideParticipantId = participant.id;
          }
        }
      }
    } else {
      // For courses: check all registrations for matching employeeUuid
      for (const registration of tutorial.registrations) {
        if (!registration.participants) continue;

        for (const participant of registration.participants) {
          if (
            participant.employeeUuid === currentEmployee.uuid &&
            (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS')
          ) {
            console.assert(
              participant.id !== undefined && participant.id !== null,
              'Course participant ID must be defined',
            );
            courseParticipantId = participant.id;
            // Found a course - return immediately (courses have priority)
            return courseParticipantId;
          }
        }
      }
    }
  }

  // Return guide if found (no course was found)
  return guideParticipantId;
}
